import urls from "global/constants/UrlConstants";
import { getCallParams, makeCall } from "utils/service";

/** Request body for issuing a key. `expires_in_days` is optional; the server
 * defaults to 365 and silently clamps anything above 1095. */
export interface IssueApiKeyRequest {
  name: string;
  expires_in_days?: number;
}

/** Issue response — snake_case (differs from the list, which is camelCase).
 * `full_key` is returned exactly once and is unrecoverable afterwards. */
export interface IssuedApiKey {
  full_key: string;
  key_id: string;
  name: string;
  expires_on: string;
}

/** A row from the list endpoint — camelCase. `rateLimitPerMin` and
 * `capabilities` are omitted (not null) for self-issued keys. */
export interface ApiKeyListItem {
  id: string;
  keyId: string;
  account: string;
  app: string;
  creatorUserEmail: string;
  name: string;
  status: string; // "Active" | "Revoked"
  expiresOn: string;
  rateLimitPerMin?: number;
  capabilities?: string[];
}

export const ISSUED_KEY_STATUS = {
  active: "Active",
  revoked: "Revoked",
};

/** POST /secure/api-keys — issue a new key. */
export const issueApiKey = async (
  data: IssueApiKeyRequest,
): Promise<IssuedApiKey> => {
  try {
    const callParams = await getCallParams("POST", data);
    const response: IssuedApiKey = await makeCall(urls.API_KEYS, callParams);
    return response;
  } catch (error: any) {
    throw error;
  }
};

/** GET /secure/api-keys — the caller's own keys, newest first. Includes
 * revoked keys; the UI filters them. */
export const fetchApiKeys = async (): Promise<ApiKeyListItem[]> => {
  try {
    const callParams = await getCallParams("GET");
    const response = await makeCall(urls.API_KEYS, callParams);
    return Array.isArray(response) ? response : [];
  } catch (error: any) {
    throw error;
  }
};

/** DELETE /secure/api-keys/{keyId} — revoke. Note the path param is the
 * `keyId` (`kid_...`), NOT the `id` UUID. Immediate and irreversible. */
export const revokeApiKey = async (keyId: string) => {
  try {
    const callParams = await getCallParams("DELETE");
    const response = await makeCall(
      `${urls.API_KEYS}/${encodeURIComponent(keyId)}`,
      callParams,
    );
    return response;
  } catch (error: any) {
    throw error;
  }
};
